import { useMemo } from 'react';
import { useGetBacnetUiTemplate } from '../useBacnetUiTemplatesApi';
import { useGetBacnetParamTemplate } from '../useBacnetParamTemplatesApi';

/**
 * Hook for managing BACnet schedule templates (UI and Parameter templates)
 * Uses React Query hooks like the control panel for better caching and deduplication
 */
const useBacnetScheduleTemplates = (bacnetUnit) => {
  // Memoize template IDs to prevent unnecessary re-renders
  const templateIds = useMemo(() => ({
    uiTemplateId: typeof bacnetUnit?.uiTemplate === 'string' ? bacnetUnit.uiTemplate : null,
    paramTemplateId: typeof bacnetUnit?.paramTemplate === 'string' ? bacnetUnit.paramTemplate : null,
  }), [bacnetUnit?.uiTemplate, bacnetUnit?.paramTemplate]);

  // Use React Query hooks like the control panel for automatic caching and deduplication
  const { data: fetchedUiTemplate, isLoading: isLoadingUi, error: uiTemplateError } =
    useGetBacnetUiTemplate(templateIds.uiTemplateId);

  const uiTemplate =
    typeof bacnetUnit?.uiTemplate === 'object' && bacnetUnit?.uiTemplate !== null
      ? bacnetUnit.uiTemplate
      : fetchedUiTemplate;

  // Universal units carry their point definitions inline on the unit record
  // (paramTemplateData) instead of behind a fetchable paramTemplate id.
  const shouldFetchParamTemplate =
    !!templateIds.paramTemplateId &&
    !bacnetUnit?.paramTemplateData &&
    typeof bacnetUnit?.paramTemplate !== 'object';

  const { data: fetchedParamTemplate, isLoading: isLoadingParam, error: paramTemplateError } =
    useGetBacnetParamTemplate(shouldFetchParamTemplate ? templateIds.paramTemplateId : null);

  const paramTemplate =
    (typeof bacnetUnit?.paramTemplate === 'object' && bacnetUnit?.paramTemplate !== null
      ? bacnetUnit.paramTemplate
      : fetchedParamTemplate) ||
    (bacnetUnit?.paramTemplateData
      ? { entityTemplate: true, data: bacnetUnit.paramTemplateData }
      : undefined);

  // Combined loading state
  const isLoadingTemplates = isLoadingUi || isLoadingParam;
  
  // Combined error state
  const templateError = uiTemplateError || paramTemplateError;

  return {
    // Template data
    uiTemplate,
    paramTemplate,
    
    // State
    isLoadingTemplates,
    templateError,
  };
};

export default useBacnetScheduleTemplates;
